import { RFSData } from "@/api/rfs";
import { documentType } from "@/features/opm/utils/rfsStatusUpdater";
import { formatIsoAsWib } from "@/utils/helper/formatDate";
import { useMemo } from "react";

export interface SupportingDocumentField {
  code: string;
  label: string;
  isRequired: boolean;
  status: string;
  completedDate: string | null;
  uncompletedDate: string | null;
}

// Satu-satunya sumber urutan tampil (urutan key) + hal yang belum ada di
// checkingProfileValidations: prefix field flat supporting-document (doc),
// prefix kolom status/date di RFS (status), target folder upload, dan type
// media lama sebelum upload pakai type=code.
export const CODE_META = {
  D00: {
    doc: "abdDariKmz",
    status: "abdFromKmz",
    target: "/abddarikmz-document",
    legacyType: "abd-from-kmz",
  },
  D02: {
    doc: "abdDariDwg",
    status: "abdFromDwg",
    target: "/abddaridwg-document",
    legacyType: "abd-from-dwg",
  },
  D04: {
    doc: "abdDwg",
    status: "abd",
    target: "/abddwg-document",
    legacyType: "abd-dwg",
  },
  D06: {
    doc: "evidenceFeeder",
    status: "evidenceFeeder",
    target: "/evidencefeeder-document",
    legacyType: "evidence-feeder",
  },
  D01: {
    doc: "bakp",
    status: "bakp",
    target: "/bakp-document",
    legacyType: "bakp",
  },
  D03: {
    doc: "cmld",
    status: "cmld",
    target: "/cmld-document",
    legacyType: "cmld",
  },
  D05: {
    doc: "clampHook",
    status: "clampHook",
    target: "/clamphook-document-zip",
    legacyType: "clamp-hook",
  },
  D07: {
    doc: "evidenceOlt",
    status: "evidenceOlt",
    target: "/evidenceolt-document",
    legacyType: "evidence-olt",
  },
} satisfies Record<
  string,
  { doc: string; status: documentType; target: string; legacyType: string }
>;

export default function useCheckingProfileValidations({
  rfs,
}: {
  rfs: RFSData | undefined;
}): {
  fields: SupportingDocumentField[];
} {
  const validations =
    rfs?.trackerdataApd?.checkingProfile?.checkingProfileValidations;

  const fields = useMemo(() => {
    const codes = Object.keys(CODE_META);

    return (validations ?? [])
      .filter(
        (v) =>
          v.validations.validationType === "Supporting Document" &&
          v.validations.code in CODE_META,
      )
      .map((v): SupportingDocumentField => {
        const meta = CODE_META[v.validations.code as keyof typeof CODE_META];

        const completedDateKey = `${meta.status}CompletedDate` as keyof RFSData;
        const uncompletedDateKey =
          `${meta.status}UncompletedDate` as keyof RFSData;

        const completedDate = rfs?.[completedDateKey] as string | undefined;
        const uncompletedDate = rfs?.[uncompletedDateKey] as string | undefined;

        return {
          code: v.validations.code,
          label: v.validations.name,
          isRequired: Boolean(v.isValidateNow),
          status: determineDocumentStatus(
            v.isValidateNow,
            completedDate,
            uncompletedDate,
          ),
          completedDate: completedDate
            ? formatIsoAsWib(String(completedDate))
            : null,
          uncompletedDate: uncompletedDate
            ? formatIsoAsWib(String(uncompletedDate))
            : null,
        };
      })
      .sort((a, b) => codes.indexOf(a.code) - codes.indexOf(b.code));
  }, [rfs, validations]);

  return { fields };
}

/**
 * Fungsi untuk menentukan status dokumen berdasarkan aturan validasi
 *
 * @param isValidateNow - Apakah dokumen perlu divalidasi sekarang (true/false/undefined)
 * @param completedDate - Tanggal dokumen selesai divalidasi
 * @param uncompletedDate - Tanggal dokumen belum selesai divalidasi
 * @returns Status dokumen: "Validated", "Unvalidated", atau "Optional"
 */
function determineDocumentStatus(
  isValidateNow: boolean | undefined,
  completedDate: unknown,
  uncompletedDate: unknown,
): string {
  // Helper functions untuk pengecekan yang lebih mudah dibaca
  const hasCompletedDate =
    completedDate !== null && completedDate !== undefined;
  const hasUncompletedDate =
    uncompletedDate !== null && uncompletedDate !== undefined;
  const hasNoDate = !hasCompletedDate && !hasUncompletedDate;

  // Jika IsValidateNow = true (Is Required Now)
  if (isValidateNow === true) {
    // Status "Validated": ada completed date DAN tidak ada uncompleted date
    if (hasCompletedDate && !hasUncompletedDate) {
      return "Validated";
    }
    // Status "Unvalidated": tidak ada completed date DAN tidak ada uncompleted date
    else if (hasNoDate) {
      return "Unvalidated";
    }
    // Kondisi lainnya juga dianggap "Unvalidated"
    else {
      return "Unvalidated";
    }
  }

  // Jika IsValidateNow = false (Is Required Later)
  else if (isValidateNow === false) {
    // Status "Validated": ada completed date DAN tidak ada uncompleted date
    if (hasCompletedDate && !hasUncompletedDate) {
      return "Validated";
    }
    // Status "Unvalidated": tidak ada completed date DAN ada uncompleted date
    else if (!hasCompletedDate && hasUncompletedDate) {
      return "Unvalidated";
    }
    // Kondisi lainnya juga dianggap "Unvalidated"
    else {
      return "Unvalidated";
    }
  }

  // Jika IsValidateNow = null/undefined (Is Required Now & Is Required Later tidak dipilih)
  else {
    return "Optional";
  }
}
